import positron from './positron.svelte';

/**
 * Creates a dark blue + orange map style for the slides backdrop.
 * Deep-clones positron and remaps all colors to a dark palette
 * with #1a1a2e blues and #E8491C orange accents.
 */

// Color mappings: positron light → slides dark
const colorMap: Record<string, string> = {
	// Background & land
	'rgb(242,243,240)': '#1a1a2e',

	// Parks & landcover
	'rgb(230, 233, 229)': '#1e1e36',
	'rgb(220, 233, 240)': '#1a1a32',  // ice shelf
	'rgb(225, 236, 244)': '#1a1a30',  // glacier

	// Residential
	'rgb(234, 234, 230)': '#1c1c34',
	'rgba(234, 234, 230, 0.6)': 'rgba(28, 28, 52, 0.6)',

	// Wood
	'rgb(220, 224, 220)': '#1e1e36',

	// Water
	'rgb(194, 200, 202)': '#12122a',
	'rgb(157, 169, 177)': '#1a1a3a',  // waterway

	// Buildings
	'rgb(234, 234, 229)': '#22223a',
	'rgb(216, 216, 211)': '#282848',  // building outline

	// Roads & highways
	'rgb(213,213,213)': 'rgba(255, 255, 255, 0.06)',
	'#dddddd': 'rgba(255, 255, 255, 0.04)',
	'hsl(0, 0%, 88%)': 'rgba(255, 255, 255, 0.05)',
	'#e9ac77': 'rgba(255, 255, 255, 0.1)',
	'#e6e6e6': 'rgba(255, 255, 255, 0.04)',
	'#cfcfcf': 'rgba(255, 255, 255, 0.06)',
	'hsl(36, 6%, 74%)': 'rgba(255, 255, 255, 0.07)',

	// Tunnel casings
	'rgb(213, 213, 213)': 'rgba(255, 255, 255, 0.03)',

	// Aeroway
	'rgba(229, 228, 224, 0.76)': 'rgba(255, 255, 255, 0.03)',

	// Road areas / pier
	'rgb(234, 234, 231)': '#1e1e36',
	'rgb(233,250,254)': '#1a1a32',

	// Motorway
	'#e0a058': 'rgba(255, 255, 255, 0.12)',

	// Railway
	'#bbb': 'rgba(255, 255, 255, 0.07)',
	'#a5b0af': 'rgba(255, 255, 255, 0.05)',

	// Boundaries
	'rgb(230, 204, 207)': 'rgba(232, 73, 28, 0.15)',
	'rgb(219, 189, 193)': 'rgba(232, 73, 28, 0.2)',

	// Text / labels
	'rgb(0,0,0)': 'rgba(232, 73, 28, 0.5)',
	'#333': 'rgba(232, 73, 28, 0.5)',

	// Halo
	'rgba(255,255,255,0.8)': 'rgba(26, 26, 46, 0.8)',
	'rgba(255,255,255,0.75)': 'rgba(26, 26, 46, 0.75)',
	'rgba(236,236,234,0.7)': 'rgba(26, 26, 46, 0.7)',
};

function remapColor(val: any): any {
	if (typeof val === 'string') {
		// Exact match
		if (colorMap[val]) return colorMap[val];

		// Normalize and try again
		const normalized = val.replace(/\s+/g, '').toLowerCase();
		for (const [k, v] of Object.entries(colorMap)) {
			if (k.replace(/\s+/g, '').toLowerCase() === normalized) return v;
		}

		// Fallback: remap any remaining rgb whites/grays to dark blues
		const rgbMatch = val.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
		if (rgbMatch) {
			const [, r, g, b] = rgbMatch.map(Number);
			const avg = (r + g + b) / 3;
			if (avg > 180) {
				// Light color → dark blue variant
				const factor = 1 - (avg / 255);
				const nr = Math.round(26 + factor * 20);
				const ng = Math.round(26 + factor * 20);
				const nb = Math.round(46 + factor * 20);
				return `rgb(${nr}, ${ng}, ${nb})`;
			}
		}

		// Fallback for hsl grays
		const hslMatch = val.match(/^hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)$/);
		if (hslMatch) {
			const lightness = Number(hslMatch[3]);
			if (lightness > 60) {
				return `rgba(255, 255, 255, ${((100 - lightness) / 100 * 0.12).toFixed(2)})`;
			}
		}

		return val;
	}
	return val;
}

function remapValue(val: any): any {
	if (val === null || val === undefined) return val;

	if (typeof val === 'string') return remapColor(val);

	if (Array.isArray(val)) {
		// MapLibre expressions like ["interpolate", ...] or ["match", ...]
		return val.map(item => remapValue(item));
	}

	if (typeof val === 'object') {
		const result: any = {};
		for (const [k, v] of Object.entries(val)) {
			result[k] = remapValue(v);
		}
		return result;
	}

	return val;
}

function createSlidesStyle(): any {
	// Deep clone
	const style = JSON.parse(JSON.stringify(positron));
	style.name = 'Slides Dark';
	style.id = 'slides-dark';

	for (const layer of style.layers) {
		if (layer.paint) {
			for (const [key, val] of Object.entries(layer.paint)) {
				if (key.includes('color') || key.includes('halo')) {
					layer.paint[key] = remapValue(val);
				}
				// Reduce opacity of text layers
				if (key === 'text-opacity' || key === 'icon-opacity') {
					layer.paint[key] = 0.4;
				}
			}
		}

		// Reduce text size slightly and lower opacity for label layers
		if (layer.type === 'symbol' && layer.paint) {
			if (!layer.paint['text-opacity']) {
				layer.paint['text-opacity'] = 0.4;
			}
		}
	}

	return style;
}

const slidesMapStyle = createSlidesStyle();
export default slidesMapStyle;
